// Stocky に検証済みの tax type CSV export が無い場合の、手作り表専用 fallback。
// 入力変更の revision と request ID を分け、遅れて返った preview/dry-run が別の
// 入力を「確認済み」にしない。実適用は dry-run で確認したsnapshotだけを送る。
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  ChoiceList,
  DataTable,
  InlineStack,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import { useCallback, useMemo, useRef, useState } from "react";

import { ApiError } from "../../api/client";
import {
  applyManualPurchaseTaxTypes,
  fileToBase64,
  previewManualPurchaseTaxTypes,
  type ManualTaxTypeImportResult,
  type ManualTaxTypeMapping,
  type ManualTaxTypePreview,
  type ManualTaxTypeSource,
} from "../../api/stocky_tax_type_manual_import";
import { csvErrorMessage } from "../../components/csvImportSpec";
import { t } from "../../lib/i18n";

type SourceMode = "csv_file" | "pasted_table";
type BusyPhase = "preview" | "dry_run" | "apply" | null;

interface MappingState {
  name: string;
  ratePercent: string;
  stockyTaxTypeId: string;
  purpose: string;
}

interface DryRunSnapshot {
  revision: number;
  source: ManualTaxTypeSource;
  mapping: ManualTaxTypeMapping;
  purchaseOnlyAttested: boolean;
  sourceImportId: string;
  sourceFileName: string;
  sourceMediaType: string;
  result: ManualTaxTypeImportResult;
}

interface Props {
  onImported: () => void;
}

const EMPTY_MAPPING: MappingState = {
  name: "",
  ratePercent: "",
  stockyTaxTypeId: "",
  purpose: "",
};

const PURPOSE_HEADER_MARKERS = [
  "purpose",
  "tax purpose",
  "purchase/sales",
  "sales/purchase",
  "用途",
  "仕入販売区分",
];

function purposeLikeHeaders(headers: readonly string[]): string[] {
  const matches: string[] = [];
  for (const header of headers) {
    const normalized = header.normalize("NFKC").trim().toLocaleLowerCase();
    if (PURPOSE_HEADER_MARKERS.some((marker) => normalized.includes(marker))) {
      matches.push(header);
    }
  }
  return matches;
}

function guessedHeader(
  preview: ManualTaxTypePreview,
  key: string,
): string {
  const guessed = preview.guessed_mapping[key];
  return typeof guessed === "string" && preview.headers.includes(guessed)
    ? guessed
    : "";
}

function manualTaxTypeError(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return t(
      "この手動税区分画面に対応するサーバー機能がまだ反映されていません。古い取込先へは切り替えず、安全のため停止しました。時間をおいて画面を再読み込みしてください。",
    );
  }
  return csvErrorMessage(error);
}

export function ManualTaxTypeImport({ onImported }: Props) {
  const inputRevisionRef = useRef(0);
  const requestIdRef = useRef(0);
  const sourceImportIdRef = useRef(globalThis.crypto.randomUUID());
  const [sourceMode, setSourceMode] = useState<SourceMode>("csv_file");
  const [fileName, setFileName] = useState("");
  const [fileMediaType, setFileMediaType] = useState("text/csv");
  const [fileBase64, setFileBase64] = useState("");
  const [pastedText, setPastedText] = useState("");
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [preview, setPreview] = useState<ManualTaxTypePreview | null>(null);
  const [mapping, setMapping] = useState<MappingState>(EMPTY_MAPPING);
  const [purchaseOnlyAttested, setPurchaseOnlyAttested] = useState(false);
  const [dryRun, setDryRun] = useState<DryRunSnapshot | null>(null);
  const [result, setResult] = useState<ManualTaxTypeImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyPhase, setBusyPhase] = useState<BusyPhase>(null);

  const invalidatePendingRequests = useCallback((): number => {
    inputRevisionRef.current += 1;
    requestIdRef.current += 1;
    setBusyPhase(null);
    return inputRevisionRef.current;
  }, []);

  const clearAfterSourceChange = useCallback(() => {
    setPreview(null);
    setMapping(EMPTY_MAPPING);
    setPurchaseOnlyAttested(false);
    setDryRun(null);
    setResult(null);
    setError(null);
  }, []);

  const clearAfterConfirmationInputChange = useCallback(() => {
    setDryRun(null);
    setResult(null);
    setError(null);
  }, []);

  const currentSource = useMemo<ManualTaxTypeSource | null>(() => {
    if (sourceMode === "csv_file") {
      return fileBase64
        ? { kind: "csv_file", fileBase64, sourceName: fileName }
        : null;
    }
    return pastedText.trim()
      ? {
          kind: "pasted_table",
          pastedText,
          sourceName: "pasted table",
        }
      : null;
  }, [fileBase64, fileName, pastedText, sourceMode]);

  const mappingPayload = useMemo<ManualTaxTypeMapping>(
    () => ({
      name: mapping.name,
      rate_percent: mapping.ratePercent,
      stocky_tax_type_id: mapping.stockyTaxTypeId || null,
      purpose: mapping.purpose || null,
    }),
    [mapping],
  );

  const mappedColumns = [
    mapping.name,
    mapping.ratePercent,
    mapping.stockyTaxTypeId,
    mapping.purpose,
  ].filter(Boolean);
  const hasDuplicateMapping =
    new Set(mappedColumns).size !== mappedColumns.length;
  const detectedPurposeHeaders = preview
    ? purposeLikeHeaders(preview.headers)
    : [];
  const purposeHeadersAmbiguous = detectedPurposeHeaders.length > 1;
  const detectedPurposeHeader =
    detectedPurposeHeaders.length === 1 ? detectedPurposeHeaders[0] : null;
  const purposeHeaderMismatch =
    detectedPurposeHeader !== null && mapping.purpose !== detectedPurposeHeader;
  const isMappingReady =
    preview !== null &&
    mapping.name !== "" &&
    mapping.ratePercent !== "" &&
    !hasDuplicateMapping &&
    !purposeHeadersAmbiguous &&
    !purposeHeaderMismatch &&
    (mapping.purpose !== "" || purchaseOnlyAttested);
  const dryRunCanApply =
    dryRun !== null &&
    dryRun.revision === inputRevisionRef.current &&
    dryRun.result.valid_rows > 0 &&
    dryRun.result.invalid_row_count === 0;
  const isApplying = busyPhase === "apply";

  const handleSourceModeChange = useCallback(
    (selected: string[]) => {
      const next = selected[0];
      if (next !== "csv_file" && next !== "pasted_table") return;
      if (next === sourceMode) return;
      invalidatePendingRequests();
      sourceImportIdRef.current = globalThis.crypto.randomUUID();
      setSourceMode(next);
      setFileName("");
      setFileMediaType("text/csv");
      setFileBase64("");
      setPastedText("");
      setIsReadingFile(false);
      clearAfterSourceChange();
    }, [clearAfterSourceChange, invalidatePendingRequests, sourceMode],
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      const revision = invalidatePendingRequests();
      setFileName(file?.name ?? "");
      sourceImportIdRef.current = globalThis.crypto.randomUUID();
      setFileMediaType(file?.type || "text/csv");
      setFileBase64("");
      setIsReadingFile(file !== null);
      clearAfterSourceChange();
      if (file === null) return;
      try {
        const encoded = await fileToBase64(file);
        if (inputRevisionRef.current !== revision) return;
        setFileBase64(encoded);
      } catch (caught) {
        if (inputRevisionRef.current !== revision) return;
        setError(manualTaxTypeError(caught));
      } finally {
        if (inputRevisionRef.current === revision) setIsReadingFile(false);
      }
    }, [clearAfterSourceChange, invalidatePendingRequests],
  );

  const handlePastedTextChange = useCallback(
    (value: string) => {
      invalidatePendingRequests();
      sourceImportIdRef.current = globalThis.crypto.randomUUID();
      setPastedText(value);
      clearAfterSourceChange();
    }, [clearAfterSourceChange, invalidatePendingRequests],
  );

  const handleMappingChange = useCallback(
    (key: keyof MappingState, value: string) => {
      if (isApplying) return;
      invalidatePendingRequests();
      sourceImportIdRef.current = globalThis.crypto.randomUUID();
      setMapping((current) => ({ ...current, [key]: value }));
      clearAfterConfirmationInputChange();
    }, [clearAfterConfirmationInputChange, invalidatePendingRequests, isApplying],
  );

  const handleAttestationChange = useCallback(
    (checked: boolean) => {
      if (isApplying) return;
      invalidatePendingRequests();
      sourceImportIdRef.current = globalThis.crypto.randomUUID();
      setPurchaseOnlyAttested(checked);
      clearAfterConfirmationInputChange();
    }, [clearAfterConfirmationInputChange, invalidatePendingRequests, isApplying],
  );

  const handlePreview = useCallback(async () => {
    if (currentSource === null || isReadingFile) return;
    const revision = inputRevisionRef.current;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setBusyPhase("preview");
    setError(null);
    setPreview(null);
    setDryRun(null);
    setResult(null);
    try {
      const checked = await previewManualPurchaseTaxTypes(currentSource);
      if (
        inputRevisionRef.current !== revision ||
        requestIdRef.current !== requestId
      ) {
        return;
      }
      setPreview(checked);
      setMapping({
        name: guessedHeader(checked, "name"),
        ratePercent: guessedHeader(checked, "rate_percent"),
        stockyTaxTypeId: guessedHeader(checked, "stocky_tax_type_id"),
        purpose: guessedHeader(checked, "purpose"),
      });
      setPurchaseOnlyAttested(false);
    } catch (caught) {
      if (
        inputRevisionRef.current === revision &&
        requestIdRef.current === requestId
      ) {
        setError(manualTaxTypeError(caught));
      }
    } finally {
      if (requestIdRef.current === requestId) setBusyPhase(null);
    }
  }, [currentSource, isReadingFile]);

  const handleDryRun = useCallback(async () => {
    if (!isMappingReady || currentSource === null) return;
    const revision = inputRevisionRef.current;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const sourceSnapshot = currentSource;
    const mappingSnapshot = { ...mappingPayload };
    const attestationSnapshot =
      mappingSnapshot.purpose == null ? purchaseOnlyAttested : false;
    const sourceImportIdSnapshot = sourceImportIdRef.current;
    const sourceFileNameSnapshot =
      sourceSnapshot.kind === "csv_file"
        ? fileName
        : "stocky-purchase-tax-types-paste.txt";
    const sourceMediaTypeSnapshot =
      sourceSnapshot.kind === "csv_file" ? fileMediaType : "text/plain";
    setBusyPhase("dry_run");
    setError(null);
    setDryRun(null);
    setResult(null);
    try {
      const checked = await applyManualPurchaseTaxTypes(
        sourceSnapshot,
        mappingSnapshot,
        {
          dryRun: true,
          purchaseOnlyAttested: attestationSnapshot,
          sourceImportId: sourceImportIdSnapshot,
          sourceFileName: sourceFileNameSnapshot,
          sourceMediaType: sourceMediaTypeSnapshot,
        },
      );
      if (!checked.dry_run || !checked.plan_token) {
        throw new Error(
          t("確認結果を検証できませんでした。まだ取り込まず、もう一度確認してください。"),
        );
      }
      if (
        inputRevisionRef.current !== revision ||
        requestIdRef.current !== requestId
      ) {
        return;
      }
      setDryRun({
        revision,
        source: sourceSnapshot,
        mapping: mappingSnapshot,
        purchaseOnlyAttested: attestationSnapshot,
        sourceImportId: sourceImportIdSnapshot,
        sourceFileName: sourceFileNameSnapshot,
        sourceMediaType: sourceMediaTypeSnapshot,
        result: checked,
      });
    } catch (caught) {
      if (
        inputRevisionRef.current === revision &&
        requestIdRef.current === requestId
      ) {
        setError(manualTaxTypeError(caught));
      }
    } finally {
      if (requestIdRef.current === requestId) setBusyPhase(null);
    }
  }, [
    currentSource,
    fileMediaType,
    fileName,
    isMappingReady,
    mappingPayload,
    purchaseOnlyAttested,
  ]);

  const handleApply = useCallback(async () => {
    if (!dryRunCanApply || dryRun === null) return;
    const revision = inputRevisionRef.current;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setBusyPhase("apply");
    setError(null);
    try {
      const applied = await applyManualPurchaseTaxTypes(
        dryRun.source,
        dryRun.mapping,
        {
          dryRun: false,
          purchaseOnlyAttested: dryRun.purchaseOnlyAttested,
          expectedPlanToken: dryRun.result.plan_token ?? undefined,
          sourceImportId: dryRun.sourceImportId,
          sourceFileName: dryRun.sourceFileName,
          sourceMediaType: dryRun.sourceMediaType,
        },
      );
      if (applied.dry_run) {
        throw new Error(
          t("取り込み結果を検証できませんでした。画面を再読み込みして確認してください。"),
        );
      }
      if (
        inputRevisionRef.current !== revision ||
        requestIdRef.current !== requestId
      ) {
        return;
      }
      setResult(applied);
      setDryRun(null);
      if (applied.created + applied.updated + applied.tax_rates_created > 0) {
        onImported();
      }
    } catch (caught) {
      if (
        inputRevisionRef.current === revision &&
        requestIdRef.current === requestId
      ) {
        if (
          caught instanceof ApiError &&
          (caught.status === 409 || caught.status === 422)
        ) {
          // DB状態を含めた再検証で競合した可能性がある。古い確認結果を再利用しない。
          setDryRun(null);
        }
        setError(manualTaxTypeError(caught));
      }
    } finally {
      if (requestIdRef.current === requestId) setBusyPhase(null);
    }
  }, [dryRun, dryRunCanApply, onImported]);

  const selectOptions = [
    { label: t("（使わない）"), value: "" },
    ...(preview?.headers.map((header) => ({ label: header, value: header })) ??
      []),
  ];
  const previewRows =
    preview?.sample_rows.map((row) =>
      preview.headers.map((_header, index) => row[index] ?? ""),
    ) ?? [];
  const previewColumnTypes =
    preview?.headers.map(() => "text" as const) ?? [];

  return (
    <section aria-label={t("仕入用税区分")}>
      <BlockStack gap="400">
        <Banner
          tone="warning"
          title={t("Stockyから書き出せる仕入用税区分CSVはありません")}
        >
          <BlockStack gap="200">
            <Text as="p">
              {t(
                "公式エクスポートに対象ファイルがなく、手元の情報で補う場合だけ purchase_tax_types.csv を作ります。1行目は Tax name, Rate %, Tax ID, Purpose です。Tax IDは分からなければ空欄にし、Purposeにはpurchaseと入力します。",
              )}
            </Text>
            <Text as="p">
              {t(
                "Purpose 列を用意して、仕入用の行だけに purchase と入力する方法を推奨します。Purpose 列を省く場合は、表の全行が仕入用であることを確認するチェックが必要です。",
              )}
            </Text>
          </BlockStack>
        </Banner>

        <Banner tone="warning" title={t("取り込み前にご確認ください")}>
          <BlockStack gap="200">
            <Text as="p">
              {t(
                "既存の商品への税率割り当てと、すでに登録済みの仕入税率は上書きしません。仕入税率がまだ1件もない場合は、安全に既定を決められる表に限って新しい仕入税率を作成し、今後の発注書で使用します。",
              )}
            </Text>
            <Text as="p">
              {t(
                "ID なしの行は名前を識別子にします。名前を変えると別の参照データとして追加され、同じ名前の税率を再取り込みしても既存の仕入税率には同期しません。Tax ID を指定した行は、作成後にこの手動機能から内容を変更できません。",
              )}
            </Text>
            <Text as="p">
              {t(
                "ID なしの税区分が複数ある場合は既定を安全に決められないため、仕入税率を自動作成しません。取り込み後に仕入税率の設定画面で必要な税率と既定を確認してください。",
              )}
            </Text>
          </BlockStack>
        </Banner>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              {t("仕入用税区分")}
            </Text>
            <BlockStack gap="100">
              <Text as="p" variant="bodySm" tone="subdued">
                {t(
                  "Tax name と Rate % は必須です。Rate % は 0〜100 のパーセントで、小数点には . を使います。Tax ID は全行で分かる場合だけ入力し、Purpose は purchase と入力してください。",
                )}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {t(
                  "ヘッダーは Tax name, Rate %, Tax ID, Purpose です。Tax ID が無い場合は列の対応付けで「（使わない）」を選んでください。",
                )}
              </Text>
            </BlockStack>
            <ChoiceList
              title={t("入力方法")}
              choices={[
                { label: t("CSV ファイルを選ぶ"), value: "csv_file" },
                {
                  label: t("ヘッダー付きの表を貼り付ける"),
                  value: "pasted_table",
                },
              ]}
              selected={[sourceMode]}
              onChange={handleSourceModeChange}
              disabled={isApplying}
            />

            {sourceMode === "csv_file" ? (
              <BlockStack gap="100">
                <Text as="p" fontWeight="semibold">
                  {t("選ぶファイル: purchase_tax_types.csv（手元で作るCSV）")}
                </Text>
                <input
                  type="file"
                  accept=".csv,text/csv,text/plain"
                  aria-label={t("仕入税区分のCSVファイル")}
                  onChange={(event) => void handleFileChange(event)}
                  disabled={isApplying}
                />
                {fileName && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t("選択中: {name}", { name: fileName })}
                  </Text>
                )}
              </BlockStack>
            ) : (
              <TextField
                label={t("ヘッダー付き表の貼り付け欄")}
                value={pastedText}
                onChange={handlePastedTextChange}
                multiline={6}
                autoComplete="off"
                placeholder={
                  "Tax name\tRate %\tTax ID\tPurpose\nStandard\t10\t123\tpurchase"
                }
                helpText={t(
                  "1行目のヘッダーを含めて、表計算ソフトからそのまま貼り付けられます。",
                )}
                disabled={isApplying}
              />
            )}

            <InlineStack align="end">
              <Button
                onClick={() => void handlePreview()}
                disabled={
                  currentSource === null ||
                  isReadingFile ||
                  busyPhase !== null
                }
                loading={isReadingFile || busyPhase === "preview"}
              >
                {t("列を確認する")}
              </Button>
            </InlineStack>

            {error && (
              <Banner tone="critical" title={t("取り込めませんでした")}>
                <Text as="p">{error}</Text>
              </Banner>
            )}

            {preview && (
              <BlockStack gap="400">
                <Banner
                  tone="info"
                  title={t("{count} 行を検出しました", {
                    count: preview.total_rows,
                  })}
                >
                  <Text as="p">
                    {t(
                      "下のサンプルを確認し、各項目に対応する列を選んでください。推測された列も確定前に確認してください。",
                    )}
                  </Text>
                </Banner>

                {preview.sample_rows.length > 0 && (
                  <DataTable
                    columnContentTypes={previewColumnTypes}
                    headings={preview.headers}
                    rows={previewRows}
                  />
                )}

                <BlockStack gap="300">
                  <Select
                    label={t("Tax name（税区分名）の列・必須")}
                    options={selectOptions}
                    value={mapping.name}
                    onChange={(value) => handleMappingChange("name", value)}
                    disabled={isApplying}
                  />
                  <Select
                    label={t("Rate %（0〜100%）の列・必須")}
                    options={selectOptions}
                    value={mapping.ratePercent}
                    onChange={(value) =>
                      handleMappingChange("ratePercent", value)
                    }
                    helpText={t(
                      "値は 0〜100 のパーセントです。5.5% は 5.5 または 5.5% と入力し、5,5 は使用できません。",
                    )}
                    disabled={isApplying}
                  />
                  <Select
                    label={t("Tax ID の列・任意")}
                    options={selectOptions}
                    value={mapping.stockyTaxTypeId}
                    onChange={(value) =>
                      handleMappingChange("stockyTaxTypeId", value)
                    }
                    helpText={t(
                      "正の Stocky tax type ID が全行で分かる場合だけ選びます。ID が無い場合は税区分名から安全な手動IDを作ります。",
                    )}
                    disabled={isApplying}
                  />
                  <Select
                    label={t("Purpose の列・任意（ある場合は必ず選択）")}
                    options={selectOptions}
                    value={mapping.purpose}
                    onChange={(value) =>
                      handleMappingChange("purpose", value)
                    }
                    helpText={t(
                      "選んだ場合、値が purchase の行だけを取り込めます。sales や空欄が1行でもあれば全体を停止します。",
                    )}
                    disabled={isApplying}
                  />
                </BlockStack>

                {hasDuplicateMapping && (
                  <Banner
                    tone="critical"
                    title={t("同じ列を複数の項目に選べません")}
                  >
                    <Text as="p">
                      {t("項目ごとに別の列を選んでください。")}
                    </Text>
                  </Banner>
                )}

                {purposeHeadersAmbiguous && (
                  <Banner
                    tone="critical"
                    title={t("Purpose と思われる列が複数あります")}
                  >
                    <Text as="p">
                      {t(
                        "用途を一意に判定できません。該当する列名を1つだけに整理してから、表を確認し直してください。",
                      )}
                    </Text>
                  </Banner>
                )}

                {purposeHeaderMismatch && (
                  <Banner
                    tone="critical"
                    title={t("Purpose と思われる列を対応付けてください")}
                  >
                    <Text as="p">
                      {t("列「{column}」を Purpose として選ぶか、表からその列を削除してから確認し直してください。チェックだけではこの列を無視できません。", {
                        column: detectedPurposeHeader ?? "",
                      })}
                    </Text>
                  </Banner>
                )}

                {mapping.purpose === "" &&
                  !purposeHeadersAmbiguous &&
                  !purposeHeaderMismatch && (
                  <Checkbox
                    label={t(
                      "この表の全行が仕入用の税区分で、販売用の行を含まないことを確認しました",
                    )}
                    checked={purchaseOnlyAttested}
                    onChange={handleAttestationChange}
                    helpText={t(
                      "Purpose 列を使わない場合だけ必要です。確認できない場合は取り込まないでください。",
                    )}
                    disabled={isApplying}
                  />
                )}

                {!isMappingReady && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t(
                      "必須列を選び、Purpose 列を対応付けるか仕入用だけの表であることを確認すると、事前確認へ進めます。",
                    )}
                  </Text>
                )}

                <InlineStack align="end">
                  <Button
                    variant="primary"
                    onClick={() => void handleDryRun()}
                    disabled={!isMappingReady || busyPhase !== null}
                    loading={busyPhase === "dry_run"}
                  >
                    {t("取り込み内容を事前確認")}
                  </Button>
                </InlineStack>
              </BlockStack>
            )}

            {dryRun && (
              <BlockStack gap="300">
                <Banner
                  tone={
                    dryRun.result.invalid_row_count === 0 ? "info" : "critical"
                  }
                  title={
                    dryRun.result.invalid_row_count === 0
                      ? t("事前確認が完了しました（まだ保存していません）")
                      : t("無効な行があるため確定できません")
                  }
                >
                  <BlockStack gap="200">
                    <Text as="p">
                      {t(
                        "全 {total} 行・有効 {valid} 行・新規 {created} 件・更新 {updated} 件・変更なし {unchanged} 件・無効 {invalid} 行",
                        {
                          total: dryRun.result.total_rows,
                          valid: dryRun.result.valid_rows,
                          created: dryRun.result.created,
                          updated: dryRun.result.updated,
                          unchanged: dryRun.result.unchanged,
                          invalid: dryRun.result.invalid_row_count,
                        },
                      )}
                    </Text>
                    {dryRun.result.tax_rates_to_create > 0 ? (
                      <Text as="p">
                        {t(
                          "確定すると仕入税率を {count} 件新規作成し、今後の発注書の税計算で使用します。商品への割り当ては変更しません。",
                          { count: dryRun.result.tax_rates_to_create },
                        )}
                      </Text>
                    ) : (
                      <Text as="p">
                        {t(
                          "確定時に仕入税率を新規作成する予定はありません。既存の仕入税率と商品への割り当ては変更しません。",
                        )}
                      </Text>
                    )}
                    {dryRun.result.invalid_rows.length > 0 && (
                      <ul>
                        {dryRun.result.invalid_rows.map((row, index) => (
                          <li key={`${row.row}-${index}`}>
                            {t("{row} 行目: {reason}", {
                              row: row.row,
                              reason: row.reason,
                            })}
                          </li>
                        ))}
                      </ul>
                    )}
                    {dryRun.result.invalid_row_count >
                      dryRun.result.invalid_rows.length && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        {t("無効な行の一覧は先頭 {shown} 件まで表示しています。", {
                          shown: dryRun.result.invalid_rows.length,
                        })}
                      </Text>
                    )}
                  </BlockStack>
                </Banner>

                <InlineStack align="end">
                  <Button
                    variant="primary"
                    onClick={() => void handleApply()}
                    disabled={!dryRunCanApply || busyPhase !== null}
                    loading={busyPhase === "apply"}
                  >
                    {t("この内容で仕入税区分を取り込む")}
                  </Button>
                </InlineStack>
              </BlockStack>
            )}

            {result && (
              <Banner
                tone={result.created + result.updated > 0 ? "success" : "info"}
                title={t("仕入税区分の取り込みが完了しました")}
              >
                <BlockStack gap="200">
                  <Text as="p">
                    {t(
                      "新規 {created} 件・更新 {updated} 件・変更なし {unchanged} 件",
                      {
                        created: result.created,
                        updated: result.updated,
                        unchanged: result.unchanged,
                      },
                    )}
                  </Text>
                  {result.tax_rates_created > 0 ? (
                    <Text as="p">
                      {t("仕入税率を {count} 件作成しました。商品への割り当ては変更していません。", {
                        count: result.tax_rates_created,
                      })}
                    </Text>
                  ) : (
                    <Text as="p">
                      {t("既存の仕入税率と商品への割り当ては変更していません。")}
                    </Text>
                  )}
                </BlockStack>
              </Banner>
            )}
          </BlockStack>
        </Card>
      </BlockStack>
    </section>
  );
}
